/**
 * SATIN ALMA ÖZETİ (kokpit) — "bugün neye bakmalıyım?".
 *
 * Bekleyen satın alma işlerini tek ekranda toplar: bekleyen/kısmi PO, onay
 * bekleyen mal girişleri, işlenmeyi bekleyen + vadesi geçmiş tedarikçi
 * faturaları, bekleyen teklif (RFQ). Kart/satıra tıklayınca ilgili ekrana
 * gider (admin:navigate CustomEvent; gr sayfası highlight_id ile vurgular).
 *
 * Backend'e ek endpoint YOK — mevcut liste endpoint'leri durum filtreleriyle
 * çağrılır, DRF pagination'ın `count` alanı okunur (page 1 yeterli, ucuz).
 * 60 sn'de bir sessizce yenilenir.
 */
import {
  Grid, Group, Loader, Paper, SimpleGrid, Stack, Table, Text, Title, UnstyledButton,
} from "@mantine/core";
import {
  IconClipboardList, IconReceipt2, IconFileInvoice,
  IconAlertTriangle, IconShoppingCart,
} from "@tabler/icons-react";
import { useResource } from "../../../shared/api";
import {
  KPICard, MoneyDisplay, PageHeader, StatusBadge, EmptyState, formatDateTime,
} from "../../../shared/ui";

const REFETCH = 60_000;

/** Sayfa geçişi — AppShell admin:navigate handler'ı params'ı sessionStorage'a
 *  yazıp goTo çağırır; ilgili sayfa highlight_id'yi okur (gr hazır). */
function navigateTo(page: string, params?: Record<string, any>) {
  window.dispatchEvent(
    new CustomEvent("admin:navigate", { detail: { page, params } }),
  );
}

/** DRF sayfalanmış cevabın toplam kayıt sayısı (AppShell.tsx:245 deseni). */
function cnt(q: { data?: any }): number {
  const d = q.data;
  return d?.count ?? (Array.isArray(d?.results) ? d.results.length : Array.isArray(d) ? d.length : 0);
}

export function ProcurementDashboardPage() {
  // Bekleyen sayım sorguları — hepsi tenant-scoped (useResource queryKey).
  const poSent = useResource<any>("purchase-orders", { filters: { status: "SENT" }, refetchInterval: REFETCH });
  const poPartial = useResource<any>("purchase-orders", { filters: { status: "PARTIAL" }, refetchInterval: REFETCH });
  const grDraft = useResource<any>("goods-receipts", { filters: { status: "DRAFT" }, refetchInterval: REFETCH });
  const invDraft = useResource<any>("supplier-invoices", { filters: { status: "DRAFT" }, refetchInterval: REFETCH });
  const invOverdue = useResource<any>("supplier-invoices", { filters: { due: "overdue" }, refetchInterval: REFETCH });
  const rfqSent = useResource<any>("rfqs", { filters: { status: "SENT" }, refetchInterval: REFETCH });
  const rfqQuotes = useResource<any>("rfqs", { filters: { status: "QUOTES_RECEIVED" }, refetchInterval: REFETCH });

  const poPendingCount = cnt(poSent) + cnt(poPartial);
  const rfqPendingCount = cnt(rfqSent) + cnt(rfqQuotes);
  const overdueCount = cnt(invOverdue);

  const loading =
    poSent.isLoading || poPartial.isLoading || grDraft.isLoading ||
    invDraft.isLoading || invOverdue.isLoading || rfqSent.isLoading || rfqQuotes.isLoading;

  const kpiValue = (n: number) => (loading ? <Loader size="sm" /> : n);

  // Acil listeleri — ilk 8 kayıt (kokpit, tam liste değil; detay ekranda).
  const grRows: any[] = (grDraft.data?.results ?? []).slice(0, 8);
  const overdueRows: any[] = (invOverdue.data?.results ?? []).slice(0, 8);

  return (
    <Stack gap="md">
      <PageHeader
        title="📊 Satın Alma Özeti"
        subtitle="Bekleyen satın alma işleri tek ekranda. Kart veya satıra tıklayınca ilgili ekrana gider."
      />

      {/* KPI kartları — KPICard'ın onClick'i yok; UnstyledButton ile sar.
          Responsive: dar ekranda 1-2, orta 3, geniş 5 kolon → yatay taşma yok. */}
      <SimpleGrid cols={{ base: 1, sm: 2, md: 3, xl: 5 }}>
        <UnstyledButton onClick={() => navigateTo("po")} style={{ textAlign: "left", display: "block", width: "100%" }}>
          <KPICard
            title="Bekleyen Siparişler"
            value={kpiValue(poPendingCount)}
            icon={<IconClipboardList size={20} />}
            color="blue"
          />
        </UnstyledButton>

        <UnstyledButton onClick={() => navigateTo("gr")} style={{ textAlign: "left", display: "block", width: "100%" }}>
          <KPICard
            title="Onay Bekleyen Mal Girişi"
            value={kpiValue(cnt(grDraft))}
            icon={<IconReceipt2 size={20} />}
            color="orange"
          />
        </UnstyledButton>

        <UnstyledButton onClick={() => navigateTo("supplier-invoices")} style={{ textAlign: "left", display: "block", width: "100%" }}>
          <KPICard
            title="İşlenmeyi Bekleyen Fatura"
            value={kpiValue(cnt(invDraft))}
            icon={<IconFileInvoice size={20} />}
            color="grape"
          />
        </UnstyledButton>

        <UnstyledButton onClick={() => navigateTo("supplier-invoices")} style={{ textAlign: "left", display: "block", width: "100%" }}>
          <KPICard
            title="Vadesi Geçmiş Fatura"
            value={kpiValue(overdueCount)}
            icon={<IconAlertTriangle size={20} />}
            color={overdueCount > 0 ? "red" : "gray"}
          />
        </UnstyledButton>

        <UnstyledButton onClick={() => navigateTo("rfq")} style={{ textAlign: "left", display: "block", width: "100%" }}>
          <KPICard
            title="Bekleyen Teklif (RFQ)"
            value={kpiValue(rfqPendingCount)}
            icon={<IconShoppingCart size={20} />}
            color="cyan"
          />
        </UnstyledButton>
      </SimpleGrid>

      {/* Acil İşlemler — iki liste, satır tıklaması ilgili ekrana götürür. */}
      <Grid>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Paper p="lg" radius="md" withBorder>
            <Group gap="xs" mb="md">
              <IconReceipt2 size={18} />
              <Title order={4}>Onay Bekleyen Mal Girişleri</Title>
            </Group>
            {grDraft.isLoading ? (
              <Group justify="center" py="xl"><Loader /></Group>
            ) : grRows.length === 0 ? (
              <EmptyState title="Onay bekleyen mal girişi yok" />
            ) : (
              <Table highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>GR No</Table.Th>
                    <Table.Th>Tedarikçi</Table.Th>
                    <Table.Th>İrsaliye</Table.Th>
                    <Table.Th>Tarih</Table.Th>
                    <Table.Th>Durum</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {grRows.map((g) => (
                    <Table.Tr
                      key={g.id}
                      style={{ cursor: "pointer" }}
                      onClick={() => navigateTo("gr", { highlight_id: g.id })}
                    >
                      <Table.Td fw={600}>{g.gr_number}</Table.Td>
                      <Table.Td>{g.supplier_name}</Table.Td>
                      <Table.Td>{g.delivery_note_number || "-"}</Table.Td>
                      <Table.Td>{g.received_at ? formatDateTime(g.received_at) : "-"}</Table.Td>
                      <Table.Td><StatusBadge status={g.status} /></Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
          </Paper>
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 6 }}>
          <Paper
            p="lg"
            radius="md"
            withBorder
            style={overdueCount > 0 ? { borderColor: "var(--mantine-color-red-5)" } : undefined}
          >
            <Group gap="xs" mb="md">
              <IconAlertTriangle size={18} color={overdueCount > 0 ? "var(--mantine-color-red-6)" : undefined} />
              <Title order={4}>Vadesi Geçmiş Faturalar</Title>
            </Group>
            {invOverdue.isLoading ? (
              <Group justify="center" py="xl"><Loader /></Group>
            ) : overdueRows.length === 0 ? (
              <EmptyState title="Vadesi geçmiş fatura yok" />
            ) : (
              <Table highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Fatura No</Table.Th>
                    <Table.Th>Tedarikçi</Table.Th>
                    <Table.Th>Vade</Table.Th>
                    <Table.Th ta="right">Toplam</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {overdueRows.map((i) => (
                    <Table.Tr
                      key={i.id}
                      style={{ cursor: "pointer" }}
                      onClick={() => navigateTo("supplier-invoices", { highlight_id: i.id })}
                    >
                      <Table.Td fw={600}>{i.invoice_number}</Table.Td>
                      <Table.Td>{i.supplier_name}</Table.Td>
                      <Table.Td>
                        <Text c="red" size="sm">
                          {i.due_date ? new Date(i.due_date).toLocaleDateString("tr-TR") : "-"}
                        </Text>
                      </Table.Td>
                      <Table.Td ta="right"><MoneyDisplay minor={i.total_minor} fw={600} /></Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
          </Paper>
        </Grid.Col>
      </Grid>
    </Stack>
  );
}
